/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Button,
    Heading,
    Stack,
    Text,
    Link as ChakraLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouteLink} from 'react-router-dom'
import {
    CONTACT_PHONE_NUMBERS,
    HOME_HREF,
    CART_PDC_PAGE_ID,
    CONTACT_US_URL
} from '../../../constants'
import useMultiSite from '../../../hooks/use-multi-site'
import {usePage} from '@salesforce/commerce-sdk-react'
import {PDCHomeCategoriesPushTile} from '../../../page-designer/assets'
import {PDCHomeCategoriesPush} from '../../../page-designer/layouts'
import {PageContext, Component} from '@salesforce/commerce-sdk-react/components'
import {usePageParameters} from '@app/hooks/use-page-parameters'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcHomeCategoriesPushTile': PDCHomeCategoriesPushTile,
    'commerce_layouts.pdcHomeCategoriesPush': PDCHomeCategoriesPush
}

const TelLink = ({tel}) => <ChakraLink href={`tel:${tel}`}>{tel}</ChakraLink>

TelLink.propTypes = {
    tel: PropTypes.string.isRequired
}

const EmailLink = ({chunks}) => {
    const {buildUrl} = useMultiSite()
    return (
        <ChakraLink as={RouteLink} href={buildUrl(CONTACT_US_URL)} to={buildUrl(CONTACT_US_URL)}>
            {chunks}
        </ChakraLink>
    )
}

EmailLink.propTypes = {
    chunks: PropTypes.node.isRequired
}

/**
 * EmptyCart is a React functional component that renders a user interface for an empty shopping cart.
 * It provides options for users to continue shopping or sign in to view saved items.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} props.isRegistered - A flag indicating whether the user is registered or not.
 *
 * @returns {JSX.Element} A JSX element representing the empty cart UI.
 *
 * The component utilizes several hooks and context:
 * - `useMultiSite`: To retrieve site-specific information such as site alias and locale.
 * - `usePage`: To fetch page designer components for the empty cart page.
 * - `useState`: To manage the context value for page designer components.
 *
 * The UI includes:
 * - A heading indicating the cart is empty.
 * - Text prompts encouraging the user to sign in or continue shopping.
 * - Contact information with a phone number and email link for assistance.
 * - Buttons for navigating to the home page or sign-in page.
 *
 * If there are additional components defined in the page designer for the empty cart,
 * they are rendered dynamically using the `PageContext.Provider`.
 */
const EmptyCart = ({isRegistered}) => {
    const {site, buildUrl, locale} = useMultiSite()

    // Get empty cart page designer components
    const parameters = usePageParameters(CART_PDC_PAGE_ID, {includeCountry: true})

    const {data: cartPage} = usePage({parameters})
    const emptyCart = cartPage?.regions?.filter((region) => region.id === 'emptyCart')[0]
        ?.components
    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})

    // Get phone number according to the site's alias and locales.
    // Defaults to the `en` phone number if a locale-specific one is not found.
    const phoneNumbers = CONTACT_PHONE_NUMBERS
    const tel = phoneNumbers[site.alias][locale.alias] || phoneNumbers[site.alias].en

    return (
        <Box data-testid="sf-cart-empty" width="full" background="whiteSmoke">
            <Stack
                spacing={8}
                p={['24px 10px 32px', null, null, '48px 0 56px']}
                alignItems={'center'}
            >
                <Stack spacing={2}>
                    <Heading
                        as="h1"
                        align="center"
                        size={['3xl', null, null, '4xl']}
                        textTransform={'uppercase'}
                    >
                        <FormattedMessage
                            defaultMessage="Your cart is empty."
                            id="empty_cart.description.empty_cart"
                        />
                    </Heading>

                    <Text align="center" variant={'bodyBase2'} mt="24px">
                        {isRegistered ? (
                            <FormattedMessage
                                defaultMessage="Retrieve your saved items or continue shopping."
                                id="empty_cart.message.for_user.continue_shopping"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Sign in to retrieve your saved items or continue shopping."
                                id="empty_cart.message.continue_shopping"
                            />
                        )}
                    </Text>
                    <Text align="center" variant={'bodyBase2'} mt="8px">
                        <FormattedMessage
                            id="empty_cart.message.need_help"
                            defaultMessage="Need help? Call us <tel></tel> or send us an <email>E-mail</email>"
                            values={{
                                tel: () => <TelLink tel={tel} />,
                                email: (chunks) => <EmailLink chunks={chunks} />
                            }}
                        />
                    </Text>
                </Stack>
                <Stack
                    justify="center"
                    spacing={16}
                    mt="24px"
                    width={['full', null, 'fit-content']}
                >
                    <Button
                        as={RouteLink}
                        variant="primary"
                        to={`/${site.alias}/${locale.alias}${HOME_HREF}`}
                    >
                        <FormattedMessage
                            defaultMessage="Continue Shopping"
                            id="empty_cart.link.continue_shopping"
                        />
                    </Button>
                    <Button
                        as={RouteLink}
                        to={isRegistered ? buildUrl('/account/wishlist') : buildUrl('/login')}
                        variant="secondary"
                    >
                        {isRegistered ? (
                            <FormattedMessage
                                defaultMessage="View saved items"
                                id="empty_cart.link.view_items"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Sign in to view saved items"
                                id="empty_cart.link.sign_in"
                            />
                        )}
                    </Button>
                </Stack>
            </Stack>
            {emptyCart &&
                emptyCart.length > 0 &&
                emptyCart.map((component) => (
                    <PageContext.Provider value={contextValue} key={component.id}>
                        <Component component={component} />
                    </PageContext.Provider>
                ))}
        </Box>
    )
}

EmptyCart.propTypes = {isRegistered: PropTypes.bool}

export default EmptyCart
